import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { connect } from 'react-redux'
import { EduJoyride } from '@edulastic/common'
import { tutorials } from '@edulastic/constants'
import { ACTIONS, EVENTS, LIFECYCLE } from 'react-joyride'
import {
  getNewDashboardJoyrideVisible,
  getTotalAssignmentCount,
  isTeacherDashboardLoading,
  isTeacherDashboardSettled as getIsTeacherDashboardSettled,
} from '../ducks'
import { getUserId } from '../../src/selectors/user'
import {
  isTestCreationLifecycleBannerDismissed,
  newDashboardOverviewSteps,
  newDashboardOverviewStepsWithoutLifecycleBanner,
  PROFILE_REPORTS_JOYRIDE_START_DELAY_MS,
} from '../constants'
import { setViewedTutorialsAction } from '../../../tutorials/tutorialReducer'
import {
  advanceDashboardJoyrideStep,
  stopJoyrideThen,
} from '../utils/dashboardJoyrideUtils'
import {
  scrollJoyrideTargetIntoView,
  waitForStableJoyrideTarget,
} from '../utils/profileReportsJoyrideUtils'

const CLASSES_TAB_TARGET = '[data-joyride-id="classesTab"]'

const NewDashboardJoyride = ({
  showJoyride,
  userId,
  setViewedTutorials,
  totalAssignmentCount,
  isLoading,
  isTeacherDashboardSettled,
}) => {
  const [isTourReady, setIsTourReady] = useState(false)
  const [joyrideRun, setJoyrideRun] = useState(false)
  const [joyrideStepIndex, setJoyrideStepIndex] = useState(0)
  const [joyrideClosed, setJoyrideClosed] = useState(false)
  const stepAdvanceTimerRef = useRef(null)
  const completeTimerRef = useRef(null)

  const hideLifecycleStep =
    totalAssignmentCount >= 2 || isTestCreationLifecycleBannerDismissed(userId)

  const joyrideSteps = useMemo(() => {
    if (!showJoyride || isLoading || !isTeacherDashboardSettled) {
      return []
    }
    return hideLifecycleStep
      ? newDashboardOverviewStepsWithoutLifecycleBanner
      : newDashboardOverviewSteps
  }, [hideLifecycleStep, isLoading, isTeacherDashboardSettled, showJoyride])

  const classesTabStepIndex = hideLifecycleStep ? 0 : 1
  const gridListViewStepIndex = hideLifecycleStep ? 1 : 2

  useEffect(() => {
    const clearPendingTimers = () => {
      if (stepAdvanceTimerRef.current) {
        clearTimeout(stepAdvanceTimerRef.current)
        stepAdvanceTimerRef.current = null
      }
      if (completeTimerRef.current) {
        clearTimeout(completeTimerRef.current)
        completeTimerRef.current = null
      }
    }

    if (!showJoyride || !joyrideSteps.length) {
      clearPendingTimers()
      setIsTourReady(false)
      setJoyrideRun(false)
      setJoyrideStepIndex(0)
      setJoyrideClosed(false)
      return undefined
    }

    let cancelled = false
    let startTimer
    const firstTarget = joyrideSteps[0]?.target

    setIsTourReady(false)
    setJoyrideRun(false)
    setJoyrideStepIndex(0)
    setJoyrideClosed(false)

    waitForStableJoyrideTarget(firstTarget).then((target) => {
      if (cancelled || !target) return

      scrollJoyrideTargetIntoView(target)
      startTimer = setTimeout(() => {
        if (!cancelled) {
          setIsTourReady(true)
          setJoyrideRun(true)
        }
      }, PROFILE_REPORTS_JOYRIDE_START_DELAY_MS)
    })

    return () => {
      cancelled = true
      clearTimeout(startTimer)
      clearPendingTimers()
      setIsTourReady(false)
      setJoyrideRun(false)
    }
  }, [showJoyride, joyrideSteps])

  const markOverviewComplete = useCallback(() => {
    setViewedTutorials([tutorials.newDashboardOverviewSteps])
  }, [setViewedTutorials])

  const finishOverviewJoyride = useCallback(() => {
    if (completeTimerRef.current) {
      clearTimeout(completeTimerRef.current)
    }
    completeTimerRef.current = stopJoyrideThen(
      () => setJoyrideRun(false),
      markOverviewComplete
    )
  }, [markOverviewComplete])

  const skipToNextStep = useCallback(
    (currentIndex) => {
      const nextStepIndex = currentIndex + 1
      if (nextStepIndex >= joyrideSteps.length) {
        finishOverviewJoyride()
        return
      }

      if (stepAdvanceTimerRef.current) {
        clearTimeout(stepAdvanceTimerRef.current)
      }
      stepAdvanceTimerRef.current = advanceDashboardJoyrideStep({
        steps: joyrideSteps,
        currentIndex,
        action: ACTIONS.NEXT,
        onAdvance: setJoyrideStepIndex,
      })
    },
    [finishOverviewJoyride, joyrideSteps]
  )

  const joyrideCallback = useCallback(
    (data) => {
      if (data.type === EVENTS.TARGET_NOT_FOUND) {
        skipToNextStep(data.index)
        return
      }

      if (data.type === EVENTS.STEP_AFTER) {
        if (data.action === ACTIONS.CLOSE) {
          setJoyrideClosed(true)
          finishOverviewJoyride()
          return
        }

        if (!joyrideClosed) {
          if (stepAdvanceTimerRef.current) {
            clearTimeout(stepAdvanceTimerRef.current)
          }
          stepAdvanceTimerRef.current = advanceDashboardJoyrideStep({
            steps: joyrideSteps,
            currentIndex: data.index,
            action: data.action,
            onAdvance: setJoyrideStepIndex,
          })
        }
      }

      if (
        !joyrideClosed &&
        data.lifecycle === LIFECYCLE.TOOLTIP &&
        data.action === ACTIONS.UPDATE &&
        (data.index === classesTabStepIndex ||
          data.index === gridListViewStepIndex)
      ) {
        document.querySelector(CLASSES_TAB_TARGET)?.click()
      }

      if (
        data.action === ACTIONS.CLOSE ||
        (data.lifecycle === LIFECYCLE.COMPLETE &&
          data.action === ACTIONS.NEXT &&
          data.index === joyrideSteps.length - 1)
      ) {
        finishOverviewJoyride()
      }
    },
    [
      classesTabStepIndex,
      finishOverviewJoyride,
      gridListViewStepIndex,
      joyrideClosed,
      joyrideSteps,
      skipToNextStep,
    ]
  )

  if (!showJoyride || !joyrideSteps.length || !isTourReady) {
    return null
  }

  return (
    <EduJoyride
      steps={joyrideSteps}
      locale={{
        close: 'Done',
        last: 'Done',
      }}
      disableOverlayClose
      showProgress={joyrideSteps.length > 1}
      options={{ zIndex: 1015 }}
      callback={joyrideCallback}
      continuous
      disableScrolling
      stepIndex={joyrideStepIndex}
      run={joyrideRun}
      showSkipButton
    />
  )
}

export default connect(
  (state) => ({
    showJoyride: getNewDashboardJoyrideVisible(state),
    userId: getUserId(state),
    totalAssignmentCount: getTotalAssignmentCount(state),
    isLoading: isTeacherDashboardLoading(state),
    isTeacherDashboardSettled: getIsTeacherDashboardSettled(state),
  }),
  {
    setViewedTutorials: setViewedTutorialsAction,
  }
)(NewDashboardJoyride)
